import { useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AddEditAdUnitContext } from 'features/inventory/ad-unit/contexts/AddEditAdUnitContext'
import { useFeatureFlag } from 'features/user/hooks/useFeatureFlag'
import { mapGraphQLErrors } from 'providers/graphql'
import { Schema } from '../schema'
import { toDTO } from './api/mapper'
import { UPDATE_AD_UNIT_REFRESHER } from './api/mutation'
import { Data } from './api/types'
import { UseUpdateAdUnitRefresher } from './types'

export const useUpdateAdUnitRefresher = (): UseUpdateAdUnitRefresher => {
  const { t } = useTranslation('features/adUnit')
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(AddEditAdUnitContext)
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  const { isFeatureEnabled: withControllingRefreshParamsOnAdUnitLevel } = useFeatureFlag(
    'yp2827ControlingRefreshParamsOnAdUnitLevel',
  )

  const [mutate] = useMutation(UPDATE_AD_UNIT_REFRESHER, {
    onCompleted: ({ updateAdUnitRefresher: { errors } }: Data): void => {
      setLoading(false)

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.error.update'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('form.success.update'), { variant: 'success' })
      }
    },
    onError: (): void => setLoading(false),
  })

  const update = (schema: Schema) => {
    setLoading(true)

    return mutate({
      variables: {
        input: toDTO({
          from: schema,
          withControllingRefreshParamsOnAdUnitLevel,
        }),
      },
    })
  }

  return { errors, update }
}
